import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import keycloak from '../config/keycloak.config';
import { User, UserRole } from '../types';
import { WALLET_SESSION_HEADER, getWalletSession, clearWalletSession } from './walletSession';
import { AuthClient, CompaniesClient, DeliveriesClient, DocumentsClient, RequestsClient, UsersClient } from '../web-api-client';
import { isWalletSessionError } from '../lib/error-utils';

// Wallet session error event constant
export const WALLET_SESSION_ERROR_EVENT = 'walletSessionError';

/**
 * Helper to check if a response contains wallet session errors
 */
async function checkForWalletError(response: Response): Promise<void> {
  // Only check error responses
  if (response.ok) return;
  
  try {
    // Clone the response so we can read it without consuming the original
    const clonedResponse = response.clone();
    const text = await clonedResponse.text();
    
    // Try to parse as JSON to get error details
    let errorData: any = null;
    try {
      errorData = JSON.parse(text);
    } catch {
      // If not JSON, check the text directly
      errorData = { message: text };
    }
    
    // Check if it's a wallet session error
    if (isWalletSessionError(errorData)) {
      console.log('🔓 Wallet session error detected in API response');
      
      // Clear wallet session from storage
      clearWalletSession();
      
      // Emit custom event to trigger unlock modal
      const event = new CustomEvent(WALLET_SESSION_ERROR_EVENT, { 
        detail: { error: errorData } 
      });
      window.dispatchEvent(event);
    }
  } catch (error) {
    // If we can't read the response, ignore
    console.debug('Could not check response for wallet error:', error);
  }
}

type KeycloakInstanceType = typeof keycloak;

interface KeycloakContextType {
  keycloak: KeycloakInstanceType | null;
  authenticated: boolean;
  initialized: boolean;
  token: string;
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  requiresWalletSetup: boolean;
  isSystemAdmin: boolean;
  isCompanyManager: boolean;
  isCompanyAdministrator: boolean;
  login: () => void;
  logout: () => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  setupWallet: (walletPassword: string) => Promise<void>;
}

const KeycloakContext = createContext<KeycloakContextType | undefined>(undefined);

export const useKeycloak = () => {
  const context = useContext(KeycloakContext);
  if (!context) throw new Error('useKeycloak must be used within KeycloakProvider');
  return context;
};

interface KeycloakProviderProps {
  children: ReactNode;
}

export const KeycloakProvider: React.FC<KeycloakProviderProps> = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [requiresWalletSetup, setRequiresWalletSetup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [walletSetupCompleted, setWalletSetupCompleted] = useState(false);

  // Initialize Keycloak
  useEffect(() => {
    const initKeycloak = async () => {
      try {
        const auth = await keycloak.init({ onLoad: 'check-sso', checkLoginIframe: false });
        setAuthenticated(auth);
        setInitialized(true);

        if (auth) {
          // Refresh token before API calls
          await keycloak.updateToken(70);
          await loadUserProfile();
        }
      } catch (err) {
        console.error('Keycloak init error:', err);
        setInitialized(true);
      } finally {
        setLoading(false);
      }
    };

    initKeycloak();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserProfile = async () => {
    //console.log('🔵 loadUserProfile called');
    
    if (!keycloak.tokenParsed) {
      //console.log('❌ No token parsed, exiting loadUserProfile');
      return;
    }

    const tokenParsed = keycloak.tokenParsed as any;
    /*console.log('🔑 Token parsed:', {
      sub: tokenParsed.sub,
      email: tokenParsed.email,
      resource_access: tokenParsed.resource_access
    });*/
    
    const roles = tokenParsed?.resource_access?.['nexus-ecmr-api']?.roles || [];
    //console.log('👥 Roles found:', roles);

    const isSystemAdmin = roles.includes('SystemAdministrator');

    const frontendRole: UserRole = roles.includes('SystemAdministrator')
      ? 'system_admin'
      : roles.includes('CompanyManager')
        ? 'company_manager'
        : 'employee';

    //console.log('✅ User role:', frontendRole);

    setUser({
      id: tokenParsed.sub,
      email: tokenParsed.email,
      name: tokenParsed.name || tokenParsed.preferred_username || tokenParsed.email?.split('@')[0],
      role: frontendRole,
      createdAt: new Date().toISOString(),
      companyId: tokenParsed.companyId,
      employeeId: tokenParsed.employeeId,
      walletConfigured: false, // updated later by wallet API
    });

    // Load wallet status - only for employees
    if (!isSystemAdmin) {
      //console.log('👤 User is employee, checking wallet status...');
      
      // Check if we've already completed setup in this session
      if (walletSetupCompleted) {
        //console.log('✅ Wallet setup already completed in this session, skipping check');
        setRequiresWalletSetup(false);
        setUser(u => u ? { ...u, walletConfigured: true } : u);
        return;
      }
      
      //console.log('⏳ walletSetupCompleted flag is:', walletSetupCompleted);
      
      try {
        //console.log('🌐 Making request to /api/Auth/wallet-status...');
        const walletStatus = await authorizedFetch('/api/Auth/wallet-status');
        //console.log('📡 Response received:', walletStatus.status, walletStatus.statusText);
        
        if (!walletStatus.ok) {
          //console.error('❌ Wallet status check failed:', walletStatus.status, walletStatus.statusText);
          // For employees, if we can't check, don't block them but log the error
          //console.warn('⚠️ Cannot verify wallet status, allowing access');
          setRequiresWalletSetup(false);
          return;
        }
        
        const data = await walletStatus.json();
        //console.log('📦 Wallet status response:', data);
        
        const needsSetup = !data.walletSetupCompleted && data.isEmployee;
        //console.log('🔧 Needs wallet setup:', needsSetup);
        
        setRequiresWalletSetup(needsSetup);
        setUser(u => u ? { ...u, walletConfigured: data.walletSetupCompleted } : u);
        
        // Mark as completed if already set up
        if (data.walletSetupCompleted) {
          setWalletSetupCompleted(true);
          //console.log('✅ Wallet is already configured');
        }
      } catch (err) {
        //console.error('💥 Wallet status load failed with error:', err);
        // Don't block employee access if check fails
        //console.warn('⚠️ Cannot verify wallet status, allowing access');
        setRequiresWalletSetup(false);
      }
    } else {
      //console.log('👔 User is admin/manager, skipping wallet check');
      // Admins and managers don't need wallet setup
      setRequiresWalletSetup(false);
    }
    
    //console.log('🏁 loadUserProfile completed');
  };

  // Core fetch wrapper
  const authorizedFetch = async (url: RequestInfo, init?: RequestInit): Promise<Response> => {
    const headers = init?.headers ? new Headers(init.headers) : new Headers();
    const session = getWalletSession();

    // Check Keycloak's authenticated state directly (not React state which is async)
    if (!keycloak.authenticated) {
      //console.error('❌ Keycloak not authenticated:', keycloak.authenticated);
      throw new Error('Not authenticated');
    }

    //console.log('✅ Keycloak authenticated, updating token...');
    await keycloak.updateToken(70);
    
    if (!keycloak.token) {
      //console.error('❌ No Keycloak token after update');
      throw new Error('Keycloak token missing');
    }

    //console.log('✅ Token ready, making request to:', url);
    headers.set('Authorization', `Bearer ${keycloak.token}`);
    if (session) {
      console.log('✅ [Internal] Adding wallet session header:', session.sessionToken.substring(0, 10) + '...');
      headers.set(WALLET_SESSION_HEADER, session.sessionToken);
    } else {
      console.log('⚠️ [Internal] No wallet session found when making request to:', url);
    }

    const newInit = { ...init, headers };
    const response = await fetch(url, newInit);

    // Check for wallet session errors
    await checkForWalletError(response);

    if (response.status === 401) {
      //console.warn('⚠️ Got 401, attempting token refresh...');
      try {
        await keycloak.updateToken(-1);
        if (keycloak.token) {
          headers.set('Authorization', `Bearer ${keycloak.token}`);
          const retryResponse = await fetch(url, { ...init, headers });
          // Check for wallet errors on retry as well
          await checkForWalletError(retryResponse);
          return retryResponse;
        }
      } catch {
        //console.error('❌ Token refresh failed, logging out');
        keycloak.logout();
      }
    }

    return response;
  };

  const login = () => keycloak.login();
  const logout = () => {
    setRequiresWalletSetup(false);
    setWalletSetupCompleted(false);
    clearWalletSession();
    keycloak.logout();
  };

  const hasRole = (role: string) => {
    const roles = (keycloak.tokenParsed as any)?.resource_access?.['nexus-ecmr-api']?.roles || [];
    return roles.includes(role);
  };

  const hasAnyRole = (roles: string[]) => roles.some(r => hasRole(r));

  const setupWallet = async (walletPassword: string) => {
    if (!keycloak.token) throw new Error('Not authenticated');

    const response = await authorizedFetch('/api/Auth/setup-wallet', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ walletPassword }),
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(errorData || 'Wallet setup failed');
    }
    
    //console.log('Wallet setup successful, updating state...');
    
    // Mark setup as completed to prevent re-checks
    setWalletSetupCompleted(true);
    setRequiresWalletSetup(false);
    setUser(u => u ? { ...u, walletConfigured: true } : u);
  };

  const isSystemAdmin = hasRole('SystemAdministrator');
  const isCompanyManager = hasRole('CompanyManager');
  const isCompanyAdministrator = hasRole('CompanyAdministrator');

  return (
    <KeycloakContext.Provider
      value={{
        keycloak,
        authenticated,
        initialized,
        token: keycloak.token || '',
        user,
        isAuthenticated: authenticated,
        loading,
        requiresWalletSetup,
        isSystemAdmin,
        isCompanyManager,
        isCompanyAdministrator,
        login,
        logout,
        hasRole,
        hasAnyRole,
        setupWallet,
      }}
    >
      {children}
    </KeycloakContext.Provider>
  );
};

// Export authorizedFetch for use in api-clients.ts and other modules
export const authorizedFetch = async (url: RequestInfo, init?: RequestInit): Promise<Response> => {
  const headers = init?.headers ? new Headers(init.headers) : new Headers();
  const session = getWalletSession();

  if (!keycloak.authenticated) {
    throw new Error('Not authenticated');
  }

  // Refresh token before making the request
  await keycloak.updateToken(70);

  if (!keycloak.token) {
    throw new Error('Keycloak token missing after refresh');
  }

  headers.set('Authorization', `Bearer ${keycloak.token}`);

  if (session) {
    console.log('✅ Adding wallet session header:', session.sessionToken.substring(0, 10) + '...');
    headers.set(WALLET_SESSION_HEADER, session.sessionToken);
  } else {
    console.log('⚠️ No wallet session found when making request to:', url);
  }

  const newInit = { ...init, headers };
  const response = await fetch(url, newInit);

  // Check for wallet session errors
  await checkForWalletError(response);

  // Handle 401 by forcing token refresh and retry
  if (response.status === 401) {
    try {
      await keycloak.updateToken(-1); // Force refresh
      if (keycloak.token) {
        headers.set('Authorization', `Bearer ${keycloak.token}`);
        const retryResponse = await fetch(url, { ...init, headers });
        // Check for wallet errors on retry as well
        await checkForWalletError(retryResponse);
        return retryResponse;
      }
    } catch {
      keycloak.logout();
      throw new Error('Session expired, please login again');
    }
  }

  return response;
};

// Wallet session functions that require authorizedFetch
export const unlockWallet = async (password: string, sessionDurationMinutes?: number) => {
  const response = await authorizedFetch('/api/wallet/unlock', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password, sessionDurationMinutes })
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to unlock wallet' }));
    throw new Error(error.description || error.message || 'Failed to unlock wallet');
  }
  
  const sessionData = await response.json();
  
  // CRITICAL FIX: Store the session in sessionStorage immediately
  const { setWalletSession } = await import('./walletSession');
  setWalletSession({
    sessionToken: sessionData.sessionToken,
    walletAddress: sessionData.walletAddress,
    expiresAt: new Date(sessionData.expiresAt)
  });
  
  console.log('✅ Wallet session stored after unlock:', sessionData.sessionToken.substring(0, 10) + '...');
  
  return sessionData;
};

export const lockWallet = async (): Promise<void> => {
  try {
    await authorizedFetch('/api/wallet/lock', { method: 'POST' });
  } finally {
    clearWalletSession();
  }
};

// NSwag clients with authorized fetch
const baseUrl = process.env.REACT_APP_API_URL || '';
export const authClient = new AuthClient(baseUrl, { fetch: authorizedFetch });
export const companiesClient = new CompaniesClient(baseUrl, { fetch: authorizedFetch });
export const deliveriesClient = new DeliveriesClient(baseUrl, { fetch: authorizedFetch });
export const documentsClient = new DocumentsClient(baseUrl, { fetch: authorizedFetch });
export const requestsClient = new RequestsClient(baseUrl, { fetch: authorizedFetch });
export const usersClient = new UsersClient(baseUrl, { fetch: authorizedFetch });
